// CollectionCardsBlockRenderer.tsx
// Consumer renderer for the Collection Cards block.
// Supports 6 display modes:
//   grid_6                              — flat 6-col grid, all tiles
//   grid_5_collapsible                  — flat 5-col, page title heading, collapsed to 5
//   grouped                            — grouped by application, all tiles
//   grouped_5_collapsible              — grouped by application, each collapsed to 5
//   grouped_item_collapsible           — grouped by item group, heading-only until View All
//   grouped_item_application_collapsible — item group → application nested (New Arrivals style)

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { buildCategoryUrl } from '../../api/cmsApi';
import { useTypography } from '../../../../context/TypographyContext';
import { NEW_ARRIVALS_ORDER, mapToStandardBucket, sectionAnchorId } from './itemGroupBuckets';
import { isConsumerItemGroup } from './consumerItemGroups';

// ─── Types ────────────────────────────────────────────────────────

type DisplayMode =
  | 'grid_6'
  | 'grid_5_collapsible'
  | 'grouped'
  | 'grouped_5_collapsible'
  | 'grouped_item_collapsible'
  | 'grouped_item_application_collapsible';

export type SortOption =
  | 'ALPHABETICAL_AZ'
  | 'ALPHABETICAL_ZA'
  | 'NEW_TO_OLD'
  | 'OLD_TO_NEW';

const SORT_MENU_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'ALPHABETICAL_AZ', label: 'Alphabetical A-Z' },
  { value: 'ALPHABETICAL_ZA', label: 'Alphabetical Z-A' },
  { value: 'NEW_TO_OLD', label: 'New to old' },
  { value: 'OLD_TO_NEW', label: 'Old to new' },
];

interface CollectionTile {
  category_id: string;
  category_name: string;
  collection?: string;
  application?: string;
  tile_display_media_single_link?: string;
  item_group?: string;
  item_group_name?: string;
  is_published?: boolean;
  created_at?: string;
}

// ─── Item-group grouping (New Arrivals accordion) ─────────────────
// New Arrivals is automated: it inherits whatever collections flow in from
// the Hertex system, so this groups the tiles by their item group rather than
// asking anyone to pick. Bucket a collection by its item-group code — shared
// with the Item Group Tiles block so a tile's scroll-to-section targets the
// same section id (see itemGroupBuckets).
const resolveItemGroupName = (t: CollectionTile): string =>
  mapToStandardBucket(t.item_group, t.item_group_name);

const groupByItemGroup = (tiles: CollectionTile[]): { name: string; tiles: CollectionTile[] }[] => {
  const map = new Map<string, CollectionTile[]>();
  tiles.forEach((t) => {
    const name = resolveItemGroupName(t);
    if (!map.has(name)) map.set(name, []);
    map.get(name)!.push(t);
  });
  const orderIndex = (n: string) => {
    const i = NEW_ARRIVALS_ORDER.indexOf(n);
    return i === -1 ? NEW_ARRIVALS_ORDER.length : i;
  };
  return Array.from(map.entries())
    .map(([name, groupTiles]) => ({ name, tiles: groupTiles }))
    .sort((a, b) => orderIndex(a.name) - orderIndex(b.name) || a.name.localeCompare(b.name));
};

// ─── Item-group → application grouping (New Arrivals-style nested) ──
// Two levels reconstructed from the flat application-keyed grouped_collections:
// item group first (Fabric, Wallpaper, …) then application (Curtaining, …).
// The application sub-heading uses the backend's grouping KEY (already clean and
// split, e.g. "Rugs" / "Runners") rather than a tile's raw `application` field,
// which can be pipe-delimited ("Rugs|Runners"). The item-group level is derived
// from each tile's item_group(_name), so no backend shape change is needed.
const groupEntriesByItemGroupAndApplication = (
  entries: [string, CollectionTile[]][]
): { name: string; applications: { application: string; tiles: CollectionTile[] }[] }[] => {
  // item-group name → (application key → tiles)
  const map = new Map<string, Map<string, CollectionTile[]>>();
  entries.forEach(([application, tiles]) => {
    tiles.forEach((t) => {
      const bucket = resolveItemGroupName(t);
      if (!map.has(bucket)) map.set(bucket, new Map());
      const appMap = map.get(bucket)!;
      if (!appMap.has(application)) appMap.set(application, []);
      appMap.get(application)!.push(t);
    });
  });
  const orderIndex = (n: string) => {
    const i = NEW_ARRIVALS_ORDER.indexOf(n);
    return i === -1 ? NEW_ARRIVALS_ORDER.length : i;
  };
  return Array.from(map.entries())
    .map(([name, appMap]) => ({
      name,
      applications: Array.from(appMap.entries())
        .map(([application, appTiles]) => ({ application, tiles: appTiles }))
        .sort((a, b) => a.application.localeCompare(b.application)),
    }))
    .sort((a, b) => orderIndex(a.name) - orderIndex(b.name) || a.name.localeCompare(b.name));
};

interface CollectionCardsBlockRendererProps {
  groupedCollections: Record<string, CollectionTile[]>;
  displayMode?: DisplayMode;
  enabled?: boolean;
  pageTitle?: string;
  viewMode?: 'live' | 'draft';
  maxWidth?: string;
  hoverAnimationEnabled?: boolean;
  headingEnabled?: boolean;
  headingOverride?: string;
  defaultSort?: SortOption;
}

// ─── Single tile ──────────────────────────────────────────────────

const CollectionTileItem: React.FC<{ tile: CollectionTile; viewMode?: string; hoverAnimationEnabled?: boolean }> = ({
                                                                                                                      tile,
                                                                                                                      hoverAnimationEnabled = true,
                                                                                                                    }) => {
  const navigate = useNavigate();
  const { styleFor } = useTypography();
  const displayName = tile.collection || tile.category_name || 'Untitled';
  const imageUrl = tile.tile_display_media_single_link || '';

  // Build the destination synchronously from the tile data we already have
  // (mirrors trade). Previously each tile fired getCategoryDetails on mount —
  // one CMS request PER tile — which flooded the page with dozens of identical
  // /cms-page calls just to derive a URL the tile already carries.
  const href = buildCategoryUrl(tile) || '#';

  const handleClick = (e: React.MouseEvent) => {
    // Only intercept a plain left-click so SPA routing is used for normal
    // navigation. Modifier clicks, middle-click, and right-click are left
    // to the browser, which will act on the real href above.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    navigate(href);
  };

  return (
    <Box
      component="a"
      href={href}
      onClick={handleClick}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        ...(hoverAnimationEnabled ? { '&:hover img': { transform: 'scale(1.03)' } } : {}),
      }}
    >
      <Box sx={{ width: '100%', aspectRatio: '3/4', overflow: 'hidden', position: 'relative' }}>
        {imageUrl && (
          <img
            data-product-image="true"
            src={imageUrl}
            alt={displayName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: hoverAnimationEnabled ? 'transform 0.5s ease' : undefined,
              display: 'block',
            }}
          />
        )}
      </Box>
      <Typography mt={1} sx={{ textTransform: 'capitalize', ...styleFor('accordion.collectionName') }}>
        {displayName}
      </Typography>
    </Box>
  );
};

// ─── Tile group ───────────────────────────────────────────────────

// Size of an accordion's main heading ("<Item group> Collections"). The
// application sub-headings inside it use this SAME size so they can never outgrow
// their parent (the accordion.heading token alone is 44px on a phone, bigger than
// the 28px main heading).
const ACCORDION_MAIN_HEADING_FONT_SIZE = { xs: '28px', md: '46px' };

const TileGroup: React.FC<{
  tiles: CollectionTile[];
  cols?: number;
  heading?: string;
  // Overrides the accordion.heading token's size for this group's heading.
  headingFontSize?: { xs: string; md: string };
  collapseAt?: number;
  viewMode?: string;
  hoverAnimationEnabled?: boolean;
  showSortDropdown?: boolean;
  defaultSort?: SortOption;
}> = ({
        tiles,
        cols = 6,
        heading,
        headingFontSize,
        collapseAt,
        viewMode = 'live',
        hoverAnimationEnabled = true,
        showSortDropdown = true,
        defaultSort = 'ALPHABETICAL_AZ',
      }) => {
  const [expanded, setExpanded] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>(defaultSort);
  const groupRef = useRef<HTMLDivElement>(null);
  const scrollOnCollapse = useRef(false);
  const { styleFor } = useTypography();

  // Scroll back to the group heading only AFTER the collapse has rendered.
  // Doing it inline right after setExpanded(false) runs scrollIntoView against
  // the still-expanded (taller) layout; the page-height shrink mid-animation
  // then overshoots to the top of the page (env/data-dependent).
  useEffect(() => {
    if (!expanded && scrollOnCollapse.current) {
      scrollOnCollapse.current = false;
      groupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expanded]);

  const handleToggle = () => {
    if (expanded) {
      scrollOnCollapse.current = true;
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  };

  const sorted = [...tiles].sort((a, b) => {
    const na = (a.collection || a.category_name || '').toLowerCase();
    const nb = (b.collection || b.category_name || '').toLowerCase();

    switch (sortOption) {
      case 'ALPHABETICAL_AZ':
        return na.localeCompare(nb);

      case 'ALPHABETICAL_ZA':
        return nb.localeCompare(na);

      case 'NEW_TO_OLD':
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();

      case 'OLD_TO_NEW':
        return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();

      default:
        return na.localeCompare(nb);
    }
  });

  // collapseAt != null enables collapsing; collapseAt === 0 means the group
  // starts with NO tiles shown (just the heading) until "View All" is clicked.
  const hasCollapse = collapseAt != null;
  const visible = hasCollapse && !expanded ? sorted.slice(0, collapseAt) : sorted;
  const hasMore = hasCollapse ? sorted.length > collapseAt! : false;

  return (
    <Box ref={groupRef} sx={{ width: '100%', scrollMarginTop: '120px' }}>
      {(heading || showSortDropdown) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          {heading ? (
            <Typography
              sx={{
                textTransform: 'capitalize',
                ...styleFor('accordion.heading'),
                ...(headingFontSize ? { fontSize: headingFontSize } : {}),
              }}
            >
              {heading}
            </Typography>
          ) : (
            <Box />
          )}

          {showSortDropdown && (
            <FormControl variant="standard" size="small">
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value as SortOption)}
                renderValue={() => 'Sort'}
                disableUnderline
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#000',
                  backgroundColor: 'transparent',

                  // Remove the grey background MUI leaves behind on the Select
                  // once an option has been chosen and it keeps focus.
                  '& .MuiSelect-select:focus': {
                    backgroundColor: 'transparent',
                  },

                  '&.Mui-focused': {
                    backgroundColor: 'transparent',
                  },

                  '&:hover': {
                    backgroundColor: 'transparent',
                  },

                  '& .MuiSelect-select': {
                    py: 0.5,
                    pr: '24px !important',
                  },
                }}
              >
                {SORT_MENU_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {sortOption === option.value && <CheckIcon fontSize="small" color="primary" />}
                    </ListItemIcon>
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${cols}, 1fr)` },
          gap: { xs: 2, md: 3 },
        }}
      >
        {visible.map((tile) => (
          <CollectionTileItem key={tile.category_id} tile={tile} viewMode={viewMode} hoverAnimationEnabled={hoverAnimationEnabled} />
        ))}
      </Box>

      {hasMore && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              borderTop: '1px solid #000',
              pt: 1.5,
              width: { xs: '45%', sm: '200px' },
            }}
          >
            <Button
              variant="text"
              disableRipple
              onClick={handleToggle}
              sx={{
                p: 0,
                minWidth: 0,
                color: '#000',
                '&:hover': { backgroundColor: 'transparent' },
                ...styleFor('accordion.button'),
              }}
            >
              {expanded ? 'Close' : 'View All'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Main renderer ────────────────────────────────────────────────

const CollectionCardsBlockRenderer: React.FC<CollectionCardsBlockRendererProps> = ({
                                                                                     groupedCollections,
                                                                                     displayMode = 'grid_6',
                                                                                     enabled = true,
                                                                                     pageTitle = '',
                                                                                     viewMode = 'live',
                                                                                     maxWidth,
                                                                                     hoverAnimationEnabled = true,
                                                                                     headingEnabled = true,
                                                                                     headingOverride = '',
                                                                                     defaultSort = 'ALPHABETICAL_AZ',
                                                                                   }) => {
  const { styleFor } = useTypography();
  if (!enabled) return null;
  if (!groupedCollections || Object.keys(groupedCollections).length === 0) return null;

  // Consumer only sells Fabric (005) / Wallpaper (001); drop collections from the
  // Rugs/Furniture/Homeware item groups so this matches the rest of the consumer
  // site. Trade shows everything (its own renderer is unfiltered).
  const consumerGrouped: Record<string, CollectionTile[]> = {};
  Object.entries(groupedCollections).forEach(([application, tiles]) => {
    const kept = tiles.filter((t) => isConsumerItemGroup(t.item_group, t.item_group_name, t.category_name));
    if (kept.length > 0) consumerGrouped[application] = kept;
  });
  if (Object.keys(consumerGrouped).length === 0) return null;

  const applicationEntries = Object.entries(consumerGrouped).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  const allTiles = applicationEntries.flatMap(([, tiles]) => tiles);

  const trimmedOverride = (headingOverride || '').trim();
  const flatHeading = trimmedOverride || (pageTitle ? `${pageTitle} Collections` : undefined);

  const containerSx = maxWidth
    ? { maxWidth, mx: 'auto' }
    : { ml: { xs: '5%', lg: '5rem' }, mr: { xs: '5%', lg: '5rem' } };

  if (displayMode === 'grid_6') {
    return (
      <Box sx={containerSx}>
        <TileGroup
          tiles={allTiles}
          cols={6}
          heading={headingEnabled && trimmedOverride ? trimmedOverride : undefined}
          viewMode={viewMode}
          hoverAnimationEnabled={hoverAnimationEnabled}
          defaultSort={defaultSort}
        />
      </Box>
    );
  }

  if (displayMode === 'grid_5_collapsible') {
    return (
      <Box sx={containerSx}>
        <TileGroup
          tiles={allTiles}
          cols={5}
          heading={headingEnabled ? flatHeading : undefined}
          collapseAt={5}
          viewMode={viewMode}
          hoverAnimationEnabled={hoverAnimationEnabled}
          defaultSort={defaultSort}
        />
      </Box>
    );
  }

  if (displayMode === 'grouped') {
    return (
      <Box sx={containerSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {applicationEntries.map(([application, tiles], index) => (
            <React.Fragment key={application}>
              {/* Divider between applications, for visual separation when an
                  accordion holds fewer than 5 collections. */}
              {index > 0 && <Divider sx={{ borderColor: '#E0E0E0', my: { xs: '45px', md: '80px' } }} />}
              <TileGroup
                tiles={tiles}
                cols={6}
                heading={headingEnabled ? `${application} Collections` : undefined}
                viewMode={viewMode}
                hoverAnimationEnabled={hoverAnimationEnabled}
                defaultSort={defaultSort}
              />
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  if (displayMode === 'grouped_5_collapsible') {
    return (
      <Box sx={containerSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {applicationEntries.map(([application, tiles], index) => (
            <React.Fragment key={application}>
              {index > 0 && <Divider sx={{ borderColor: '#E0E0E0', my: { xs: '45px', md: '80px' } }} />}
              <TileGroup
                tiles={tiles}
                cols={5}
                heading={headingEnabled ? `${application} Collections` : undefined}
                collapseAt={5}
                viewMode={viewMode}
                hoverAnimationEnabled={hoverAnimationEnabled}
                defaultSort={defaultSort}
              />
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  // Item-group accordion: one section per item group, each a heading with no
  // tiles until "View All" reveals the (smaller) 6-col grid. Generic across
  // pages (New Arrivals, Outdoor, …). The heading override acts as an optional
  // prefix, e.g. "New in" -> "New in Furniture"; blank -> just "Furniture".
  if (displayMode === 'grouped_item_collapsible') {
    const itemGroups = groupByItemGroup(allTiles);
    const headingFor = (name: string) => (trimmedOverride ? `${trimmedOverride} ${name}` : name);
    return (
      <Box sx={containerSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {itemGroups.map((group, index) => (
            <React.Fragment key={group.name}>
              {index > 0 && <Divider sx={{ borderColor: '#E0E0E0', my: { xs: '45px', md: '80px' } }} />}
              <Box id={sectionAnchorId(group.name)} sx={{ scrollMarginTop: '120px' }}>
                <TileGroup
                  tiles={group.tiles}
                  cols={6}
                  heading={headingEnabled ? headingFor(group.name) : undefined}
                  collapseAt={0}
                  viewMode={viewMode}
                  hoverAnimationEnabled={hoverAnimationEnabled}
                  defaultSort={defaultSort}
                />
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  // Item-group → application accordion: New Arrivals-style nested layout.
  // One "{prefix} {item group} Collections" main heading per item group, then an
  // application sub-heading per application, each with its own collapsible grid
  // (first row shown, "View All"/"Close" reveals the rest). The heading override
  // acts as a prefix on the main heading, e.g. "New" -> "New Fabric Collections".
  if (displayMode === 'grouped_item_application_collapsible') {
    const itemGroups = groupEntriesByItemGroupAndApplication(applicationEntries);
    const mainHeadingFor = (name: string) =>
      trimmedOverride ? `${trimmedOverride} ${name} Collections` : `${name} Collections`;
    return (
      <Box sx={containerSx}>
        {/* gap:0 — spacing between groups is owned by the divider's vertical
            margins (90px desktop / 45px mobile, each side of the line) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {itemGroups.map((group, index) => (
            <React.Fragment key={group.name}>
              {/* Thin divider between groups of collections */}
              {index > 0 && (
                <Divider sx={{ borderColor: '#E0E0E0', my: { xs: '45px', md: '90px' } }} />
              )}
              <Box id={sectionAnchorId(group.name)} sx={{ scrollMarginTop: '120px' }}>
                {headingEnabled && (
                  <Typography
                    sx={{
                      textTransform: 'capitalize',
                      mb: 5,
                      ...styleFor('accordion.heading'),
                      fontSize: ACCORDION_MAIN_HEADING_FONT_SIZE,
                    }}
                  >
                    {mainHeadingFor(group.name)}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {group.applications.map((app, appIndex) => (
                    <Box key={app.application}>
                      {/* The application title is rendered by TileGroup (same as
                          trade) so it takes the accordion.heading typography token
                          and shares the Sort control's row, at the SAME size as the
                          main heading above it. */}
                      <TileGroup
                        heading={headingEnabled ? app.application : undefined}
                        headingFontSize={ACCORDION_MAIN_HEADING_FONT_SIZE}
                        tiles={app.tiles}
                        cols={6}
                        collapseAt={6}
                        viewMode={viewMode}
                        hoverAnimationEnabled={hoverAnimationEnabled}
                        showSortDropdown={true}
                        defaultSort={defaultSort}
                      />
                      {appIndex < group.applications.length - 1 && (
                        <Divider sx={{ borderColor: '#E0E0E0', mt: 6, mb: 2 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  return null;
};

export default CollectionCardsBlockRenderer;